/**
 * MaintenanceUpcomingBanner — soft amber strip rendered at the top of
 * the customer layout while a maintenance window is scheduled but not
 * yet started.
 *
 * Behavior:
 *   - Returns null when context.isUpcoming is false (mode=off or
 *     mode=ongoing). Mounted unconditionally in Layout for that reason —
 *     zero visual impact in the off path.
 *   - Live countdown ticks every 30s (banner cadence is fine — this is
 *     not a stopwatch). Falls back to "soon" when the start time is
 *     within the next minute or already past (latter shouldn't happen
 *     because the context flips us to ongoing once `now ≥ starts_at`).
 *   - NOT dismissible per Plan-MaintenanceBanners §E.1: operator wants
 *     the banner visible until the window starts.
 *
 * Mobile:
 *   - <sm: stacks vertically (icon + heading on row 1, body+countdown
 *     on row 2, CTA on row 3).
 *   - sm+:  single horizontal row.
 *
 * Dark/light contrast (mandatory per `feedback_frontend_standards.md`):
 *   - Light: amber-50 bg, amber-900 text, amber-200 border.
 *   - Dark : amber-950 bg, amber-100 text, amber-800 border.
 */
import React, { useEffect, useState } from 'react';
import { Wrench, Save } from 'lucide-react';
import { useMaintenance } from '../../contexts/MaintenanceContext';

function formatCountdown(ms: number): string {
  if (ms <= 60_000) return 'soon';
  const totalMin = Math.floor(ms / 60_000);
  if (totalMin < 60) return `in ${totalMin}m`;
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h < 24) return m === 0 ? `in ${h}h` : `in ${h}h ${m}m`;
  const d = Math.floor(h / 24);
  const remH = h % 24;
  return remH === 0 ? `in ${d}d` : `in ${d}d ${remH}h`;
}

function formatLocalTime(d: Date | null): string | null {
  if (!d) return null;
  try {
    return d.toLocaleString(undefined, {
      hour: 'numeric',
      minute: '2-digit',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return d.toISOString();
  }
}

const MaintenanceUpcomingBanner: React.FC = () => {
  const { isUpcoming, startsAt, messageTitle, messageBody } = useMaintenance();
  const [now, setNow] = useState(() => Date.now());

  // Tick every 30s while we're upcoming so the countdown stays current.
  useEffect(() => {
    if (!isUpcoming) return;
    const id = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(id);
  }, [isUpcoming]);

  if (!isUpcoming) return null;

  const remainingMs = startsAt ? Math.max(0, startsAt.getTime() - now) : 0;
  const countdown = startsAt ? formatCountdown(remainingMs) : null;
  const startsLocal = formatLocalTime(startsAt);

  const title = messageTitle?.trim() || 'Scheduled maintenance';
  // Truncate long bodies inside the banner — full body lives in the
  // ongoing blocker. Soft cap so we don't push the page title off-screen.
  const body = (messageBody || '').trim();
  const shortBody = body.length > 160 ? `${body.slice(0, 157)}…` : body;

  return (
    <div
      role="status"
      aria-live="polite"
      className="bg-amber-50 dark:bg-amber-950 border-b border-amber-200 dark:border-amber-800 text-amber-900 dark:text-amber-100"
    >
      <div className="px-4 py-3 sm:px-6 sm:py-2.5 flex flex-col sm:flex-row sm:items-center sm:gap-4 gap-2">
        <div className="flex items-start sm:items-center gap-2 min-w-0">
          <Wrench
            className="w-5 h-5 shrink-0 text-amber-700 dark:text-amber-300 mt-0.5 sm:mt-0"
            aria-hidden="true"
          />
          <div className="min-w-0">
            <p className="text-sm font-semibold leading-tight">{title}</p>
            {shortBody && (
              <p className="text-xs sm:text-[13px] leading-snug opacity-90 mt-0.5 break-words">
                {shortBody}
              </p>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 sm:gap-3 sm:ml-auto sm:shrink-0 text-xs sm:text-sm">
          {countdown && (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-100 dark:bg-amber-900/60 border border-amber-200 dark:border-amber-700 font-medium whitespace-nowrap">
              Starts {countdown}
              {startsLocal ? (
                <span className="hidden sm:inline opacity-80 font-normal">
                  · {startsLocal}
                </span>
              ) : null}
            </span>
          )}
          <span
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-amber-300 dark:border-amber-700 font-medium whitespace-nowrap"
            // Visual cue only, per E.1 spec — no actual action.
            aria-hidden="true"
          >
            <Save className="w-3.5 h-3.5" aria-hidden="true" />
            Save your work
          </span>
        </div>
      </div>
    </div>
  );
};

export default MaintenanceUpcomingBanner;
